import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useTranslation } from 'react-i18next'
import { gql } from 'graphql-request'

import type { StaffSessionPolicy } from '@hapana/shared/schemas/corporates/staff-session-policy-schemas'
import { TOAST_PROPS_OPTIONS } from '@hapana/shared/web/components/shared/toast/toast.constants'
import { useToast } from '@hapana/shared/web/components/shared/toast/toast.hooks'
import { getErrorMessage } from '@hapana/shared/web/services/gqty'
import { gqlMutate, gqlQuery } from '@hapana/shared/web/services/requests.service'

export const STAFF_SESSION_POLICY_QUERY_KEY = 'STAFF_SESSION_POLICY'

const STAFF_SESSION_POLICY_FIELDS = `
  brandIdleTimeoutMinutes
  allowLocationIdleTimeout
  maxSessionHours
`

const staffSessionPolicyQuery = gql`
  query StaffSessionPolicy {
    staffSessionPolicy {
      ${STAFF_SESSION_POLICY_FIELDS}
    }
  }
`

const updateStaffSessionPolicyMutation = gql`
  mutation UpdateStaffSessionPolicy($input: UpdateStaffSessionPolicyInputInput!) {
    updateStaffSessionPolicy(input: $input) {
      ${STAFF_SESSION_POLICY_FIELDS}
    }
  }
`

type StaffSessionPolicyResponse = { staffSessionPolicy: StaffSessionPolicy }

const staffSessionPolicyRequest = gqlQuery<StaffSessionPolicyResponse>({
  key: STAFF_SESSION_POLICY_QUERY_KEY,
  query: staffSessionPolicyQuery,
  options: { isV1: false },
})

export const useStaffSessionPolicy = ({ enabled }: { enabled: boolean }) =>
  useQuery<StaffSessionPolicyResponse, Error, StaffSessionPolicy>(
    [STAFF_SESSION_POLICY_QUERY_KEY],
    () => staffSessionPolicyRequest.query({ meta: {} }),
    { enabled, select: (data) => data.staffSessionPolicy, staleTime: 5 * 60_000 }
  )

export const useUpdateStaffSessionPolicy = () => {
  const queryClient = useQueryClient()
  const { openToast } = useToast()
  const { t } = useTranslation('core')
  return useMutation<{ updateStaffSessionPolicy: StaffSessionPolicy }, Error, StaffSessionPolicy>(
    (input) =>
      gqlMutate({
        mutation: updateStaffSessionPolicyMutation,
        variables: { input },
        options: { isV1: false },
      }),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries([STAFF_SESSION_POLICY_QUERY_KEY])
        openToast({
          title: t('sessionPolicy.toast.saved'),
          severity: TOAST_PROPS_OPTIONS.SEVERITY.SUCCESS,
        })
      },
      onError: (error) =>
        openToast({
          title: getErrorMessage(error, t('sessionPolicy.toast.saveError')),
          severity: TOAST_PROPS_OPTIONS.SEVERITY.ERROR,
        }),
    }
  )
}
